import { add, isBefore } from "date-fns";

import { APPOINTMENT_NOTE_TYPES, STATUS_IDENTIFIERS } from "models";
import { DATE_ORDER_BY } from "modules/Appointments";
import { STAR_FILTERS } from "modules/Appointments/hooks/useGetStarIndicators";
import { APPOINTMENTS_STORAGE_KEYS } from "modules/Appointments/storageKeys";
import { getPreference, setPreference } from "utils/common";
import { toISOString } from "utils/dateHelperFunctions";

import type { Appointment, AppointmentStatus } from "models";
import type { IndicatorFilters } from "modules/Appointments/components";

enum SORTING_ORDER {
  ascending = "ascending",
  descending = "descending"
}

export const ACCESSOR_KEYS = {
  DateColumn: "date-column-accessor-value",
  CustomerColumn: "customer-column-accessor-value",
  MakeColumn: "make-column-accessor-value",
  LastUserColumn: "last-user-column-accessor-value"
};

export const sortAppointmentsByID = (app1: Appointment, app2: Appointment, order: SORTING_ORDER) => {
  if (app1.id < app2.id) return order === SORTING_ORDER.ascending ? -1 : 1;
  if (app1.id > app2.id) return order === SORTING_ORDER.ascending ? 1 : -1;
  return 0;
};

export const sortAppointmentsByStatus = (appointments: Appointment[], statuses: AppointmentStatus[] | undefined): Appointment[] => {
  const orderMap = new Map<STATUS_IDENTIFIERS, number>();
  statuses?.forEach(s => orderMap.set(s.identifier, s.ordering));

  return appointments.sort((prevApp, nextApp) => {
    if (statuses) {
      const aStatusOrder = orderMap.get(prevApp.appointment_status_identifier) || 0;
      const bStatusOrder = orderMap.get(nextApp.appointment_status_identifier) || 0;
      if (aStatusOrder < bStatusOrder) return -1;
      if (aStatusOrder > bStatusOrder) return 1;
    }

    const dateColumnPreferenceValue = getPreference(APPOINTMENTS_STORAGE_KEYS.DateSortPreference, DATE_ORDER_BY.APPOINTMENT_DATE_ASCENDING);
    const dateColumnAccessorKey = dateColumnPreferenceValue.replace("_asc", "").replace("_desc", "");
    const dateStringA = dateColumnAccessorKey === "time_car_app" ? prevApp.time_car_app : (prevApp.car_return_time ?? "");
    const dateStringB = dateColumnAccessorKey === "time_car_app" ? nextApp.time_car_app : (nextApp.car_return_time ?? "");

    if ([DATE_ORDER_BY.APPOINTMENT_DATE_ASCENDING, DATE_ORDER_BY.SCHEDULED_OUT_DATE_ASCENDING].includes(dateColumnPreferenceValue)) {
      const dateComparison = dateStringA.localeCompare(dateStringB);
      if (dateComparison !== 0) return dateComparison;
      return sortAppointmentsByID(prevApp, nextApp, SORTING_ORDER.ascending);
    }

    const dateComparison = dateStringB.localeCompare(dateStringA);
    if (dateComparison !== 0) return dateComparison;
    return sortAppointmentsByID(prevApp, nextApp, SORTING_ORDER.descending);
  });
};

export const sortAppointments = (appointments: Appointment[], statuses: AppointmentStatus[] | undefined): Appointment[] => {
  const panics: Appointment[] = [];
  const lockers: Appointment[] = [];
  const others: Appointment[] = [];

  appointments.forEach(appointment => {
    if (appointment.has_panic) {
      panics.push(appointment);
    } else if (
      (appointment.key_dropped_at && !appointment.key_picked_up_at) ||
      (appointment.sharebox_key_dropped_at && !appointment.sharebox_key_picked_up_at) ||
      (appointment.acses_key_dropped_at && !appointment.acses_key_picked_up_at) ||
      (appointment.kiosk_checked_in_at && !appointment.car_in_shop) ||
      (appointment.key_dropped_back_at && !appointment.key_picked_up_back_at) ||
      (appointment.sharebox_key_dropped_back_at && !appointment.sharebox_key_picked_up_back_at) ||
      (appointment.acses_key_dropped_back_at && !appointment.acses_key_picked_up_back_at)
    ) {
      lockers.push(appointment);
    } else {
      others.push(appointment);
    }
  });

  const sortedPanics = sortAppointmentsByStatus(panics, statuses);
  const sortedLockers = sortAppointmentsByStatus(lockers, statuses);
  const sortedOthers = sortAppointmentsByStatus(others, statuses);

  const regularWOs: Appointment[] = [];
  const noDriveWOs: Appointment[] = [];
  const backOrderWOs: Appointment[] = [];

  sortedOthers.forEach(appointment => {
    if (appointment.appointment_status_identifier === STATUS_IDENTIFIERS.BackOrder) {
      backOrderWOs.push(appointment);
    } else if (
      appointment.car_in_shop &&
      !appointment.car_out_of_shop &&
      appointment.notes?.some(note => note.appointment_note_type_id === APPOINTMENT_NOTE_TYPES.NoDrive)
    ) {
      noDriveWOs.push(appointment);
    } else {
      regularWOs.push(appointment);
    }
  });

  return [...sortedPanics, ...sortedLockers, ...regularWOs, ...noDriveWOs, ...backOrderWOs];
};

export const filterAppointmentsBySearch = (appointments: Appointment[], searchTerm: string = "") => {
  const filteredAppointments = [];
  for (const appointment of appointments) {
    const matchFound = Object.values(appointment).some(value => {
      if (typeof value === "object" && value !== null) {
        return Object.values(value).some(nestedValue => String(nestedValue).toLowerCase().includes(searchTerm.toLowerCase()));
      }
      return String(value).toLowerCase().includes(searchTerm.toLowerCase());
    });

    if (matchFound) {
      filteredAppointments.push(appointment);
    }
  }
  return filteredAppointments;
};

// TODO: clean this up...
export const filterSpecialIndicators = (appointments: Appointment[], specialIndicators: unknown | Record<string, string>) => {
  const specialIndicatorsKeys = Object.keys(specialIndicators || {});
  if (appointments.length > 0 && specialIndicatorsKeys.length) {
    return appointments.filter((appointment: Appointment) => {
      const onlyWithoutMoney = specialIndicatorsKeys.length === 1 && (specialIndicators as unknown as Appointment).is_money;
      let visible = onlyWithoutMoney;
      for (const icon of specialIndicatorsKeys) {
        switch (icon) {
          case "is_money":
            if (appointment.is_money) return false;
            break;

          case "pin_count":
            if (appointment.recall_pin_count > 0 || appointment.remark_pin_count > 0 || appointment.warranty_pin_count > 0) visible = true;
            break;

          case "is_shop_green":
            if (appointment.is_shop_color === "green") visible = true;
            break;

          case "is_shop_orange":
            if (appointment.is_shop_color.startsWith("#")) visible = true;
            break;

          case "is_shop_blue":
            if (appointment.is_shop_color === "blue") visible = true;
            break;

          case "is_shop_red":
            if (appointment.is_shop_color === "red") visible = true;
            break;

          case "is_shop_yellow":
            if (appointment.is_shop_color === "yellow") visible = true;
            break;

          case STAR_FILTERS.Red:
            if (appointment.is_star && appointment.is_star_color === "red") visible = true;
            break;

          case STAR_FILTERS.Blue:
            if (appointment.is_star && appointment.is_star_color === "blue") visible = true;
            break;

          case STAR_FILTERS.Green:
            if (appointment.is_star && appointment.is_star_color === "green") visible = true;
            break;

          case STAR_FILTERS.Yellow:
            if (appointment.is_star && appointment.is_star_color === "yellow") visible = true;
            break;

          case "is_recurring":
            if (appointment.is_recurring || appointment.notes?.find(n => n.appointment_note_type_id === APPOINTMENT_NOTE_TYPES.RecurringCar)) visible = true;
            break;

          default:
            if (appointment[icon as keyof typeof appointment]) visible = true;
            break;
        }
      }

      return visible;
    });
  }
  return appointments;
};

export const determineAPKColor = (date: string): string => {
  const expirationDate = toISOString(date);

  if (isBefore(expirationDate, new Date())) return "expired-apk";

  if (isBefore(expirationDate, add(new Date(), { months: 2 }))) return "apk-expire-soon";

  return "";
};

export const indicatorsFiltrationLogic = (current: IndicatorFilters[], index: number, preferenceKey: APPOINTMENTS_STORAGE_KEYS) => {
  const updatedFilters = current.map((filter, idx) => (idx === index ? { ...filter, active: !filter.active } : filter));
  const preferenceValues = updatedFilters.map(({ filter, active }) => ({ filter, active }));
  setPreference(preferenceKey, preferenceValues);

  return updatedFilters;
};

export const initializeIdentifiers = (defaultValues: IndicatorFilters[], preferenceKey: APPOINTMENTS_STORAGE_KEYS) => {
  const preferenceValues = getPreference<IndicatorFilters[]>(preferenceKey, []);
  if (!preferenceValues.length) return defaultValues;

  return defaultValues.map(defaultValue => {
    const matchingPreferenceValue = preferenceValues.find(preference => preference.filter === defaultValue.filter);
    return { ...defaultValue, active: Boolean(matchingPreferenceValue?.active) };
  });
};
